import React, { memo, useMemo } from "react";
import { Pressable, StyleSheet, View } from "react-native";
import { useNavigation } from "@react-navigation/native";
import Text from "@components/CustomText";
import ChevronRight from "@assets/svg/ChevronRight";
import { width } from "@constants/metrics";
import {
  resolveRowWidgetTheme,
  RowWidgetThemeInput,
} from "../../../utils/rowWidgetTheme";

interface RowWidgetItem extends RowWidgetThemeInput {
  title?: string;
  nav_to?: string;
  nav_params?: Record<string, unknown>;
}

interface Props {
  item: RowWidgetItem;
}

/**
 * Full-width action row (widget_type "ROW"): optional badge, title, chevron.
 * Colours and badge text come from the widget config (bg_color, text_color,
 * badge_bg_color, badge_text); see utils/rowWidgetTheme for the fallbacks.
 */
const RowWidget = ({ item }: Props) => {
  const navigation = useNavigation<{
    navigate: (name: string, params?: Record<string, unknown>) => void;
  }>();
  const target = item?.nav_to;
  const theme = useMemo(() => resolveRowWidgetTheme(item), [item]);

  return (
    <Pressable
      testID="hpw-row-widget"
      accessibilityRole="button"
      accessibilityLabel={item?.title}
      disabled={!target}
      onPress={() => {
        if (target) navigation.navigate(target, item.nav_params);
      }}
      style={({ pressed }) => [
        styles.row,
        { backgroundColor: theme.bgColor, shadowColor: theme.bgColor },
        pressed && styles.pressed,
      ]}
    >
      <View style={styles.left}>
        {theme.badgeText ? (
          <View style={[styles.badge, { backgroundColor: theme.badgeBgColor }]}>
            <Text style={[styles.badgeText, { color: theme.textColor }]}>
              {theme.badgeText}
            </Text>
          </View>
        ) : null}
        <Text
          style={[
            styles.title,
            { color: theme.textColor },
            !theme.badgeText && styles.titleNoBadge,
          ]}
          numberOfLines={1}
        >
          {item?.title}
        </Text>
      </View>
      <ChevronRight width={12} height={16} pathFill={theme.textColor} />
    </Pressable>
  );
};

export default memo(RowWidget);

const styles = StyleSheet.create({
  // Same box as LargeWidget / ExtraLargeWidget so the row lines up with them.
  row: {
    width: width - 32,
    marginHorizontal: 6,
    marginVertical: 8,
    borderRadius: 16,
    paddingVertical: 16,
    paddingHorizontal: 18,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    elevation: 3,
    shadowOpacity: 0.28,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 4 },
  },
  pressed: { opacity: 0.85 },
  left: { flexDirection: "row", alignItems: "center", flex: 1 },
  badge: {
    width: 34,
    height: 34,
    borderRadius: 9,
    alignItems: "center",
    justifyContent: "center",
  },
  badgeText: { fontSize: 11, fontWeight: "600" },
  title: {
    fontSize: 17,
    fontWeight: "700",
    marginLeft: 12,
    flex: 1,
  },
  titleNoBadge: { marginLeft: 0 },
});
